import React, { useEffect, useState } from "react";
import { api, formatApiError } from "../lib/api";
import Header from "../components/Header";
import Sidebar from "../components/Sidebar";
import { Button } from "../components/ui/button";
import {
  ShieldCheck, ShieldAlert, Upload, CheckCircle2, Clock, XCircle,
  AlertTriangle, CreditCard, Loader2, FileText,
} from "lucide-react";
import { toast } from "sonner";

// Statut d'un document (dernier téléversé par type)
function docStatus(doc) {
  if (!doc) return "missing";
  if (doc.rejected_at) return "rejected";
  if (!doc.verified_at) return "pending";
  if (doc.expires_at && doc.expires_at <= new Date().toISOString().slice(0, 10)) return "expired";
  return "verified";
}

const STATUS_UI = {
  missing:  ["Manquant",     "bg-slate-100 text-slate-600",   FileText],
  pending:  ["En attente",   "bg-amber-100 text-amber-800",   Clock],
  rejected: ["Refusé",       "bg-red-100 text-red-700",       XCircle],
  expired:  ["Expiré",       "bg-red-100 text-red-700",       AlertTriangle],
  verified: ["Validé",       "bg-emerald-100 text-emerald-800", CheckCircle2],
};

function DocRow({ docType, label, latest, required, onUploaded }) {
  const [file, setFile] = useState(null);
  const [expiresAt, setExpiresAt] = useState("");
  const [sending, setSending] = useState(false);
  const status = docStatus(latest);
  const [statusLabel, chip, Icon] = STATUS_UI[status];

  const upload = async () => {
    if (!file) { toast.error("Choisissez un fichier."); return; }
    setSending(true);
    try {
      const form = new FormData();
      form.append("doc_type", docType);
      form.append("file", file);
      if (expiresAt) form.append("expires_at", expiresAt);
      await api.post("/pro/documents", form, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`${label} téléversé — en attente de vérification`);
      setFile(null);
      setExpiresAt("");
      onUploaded();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="border border-slate-200 rounded-xl p-4 bg-white" data-testid={`doc-row-${docType}`}>
      <div className="flex items-center justify-between gap-2 mb-2">
        <div className="font-display font-bold text-sm text-slate-900">
          {label} {required && <span className="text-orange-600">*</span>}
        </div>
        <span className={`flex items-center gap-1 text-[11px] font-bold px-2 py-0.5 rounded-full ${chip}`}>
          <Icon className="w-3 h-3" />{statusLabel}
        </span>
      </div>
      {latest && (
        <div className="text-xs text-slate-500 mb-2">
          {latest.file_name} · téléversé le {new Date(latest.uploaded_at).toLocaleDateString("fr-FR")}
          {latest.expires_at && <> · expire le <strong>{latest.expires_at}</strong></>}
          {latest.reject_reason && <div className="text-red-600 mt-0.5">Motif du refus : {latest.reject_reason}</div>}
        </div>
      )}
      <div className="flex flex-wrap items-center gap-2">
        <input
          type="file"
          onChange={e => setFile(e.target.files?.[0] || null)}
          className="text-xs text-slate-600 file:mr-2 file:px-3 file:py-1.5 file:rounded-lg file:border-0 file:bg-slate-100 file:text-slate-700 file:text-xs file:font-semibold hover:file:bg-slate-200"
        />
        <input
          type="date"
          value={expiresAt}
          onChange={e => setExpiresAt(e.target.value)}
          title="Date d'expiration (si le document en a une)"
          className="border border-slate-200 rounded-lg px-2 py-1.5 text-xs text-slate-600"
        />
        <Button size="sm" onClick={upload} disabled={sending || !file} className="bg-slate-900 hover:bg-slate-800 h-8">
          {sending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />}
          <span className="ml-1">Téléverser</span>
        </Button>
      </div>
    </div>
  );
}

export default function ProConformite() {
  const [data, setData] = useState(null);
  const [connect, setConnect] = useState(null);
  const [connectLoading, setConnectLoading] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/pro/documents");
      setData(data);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
    try {
      const { data } = await api.get("/connect/status");
      setConnect(data);
    } catch { setConnect(null); }
  };

  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const onboard = async () => {
    setConnectLoading(true);
    try {
      const { data } = await api.post("/connect/onboard");
      window.location.href = data.url;
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
      setConnectLoading(false);
    }
  };

  const elig = data?.eligibility;
  const latestByType = {};
  (data?.documents || []).forEach(d => { if (!latestByType[d.doc_type]) latestByType[d.doc_type] = d; });

  return (
    <div className="min-h-screen bg-slate-50" data-testid="pro-conformite-page">
      <Header />
      <div className="flex pt-16">
        <Sidebar />
        <main className="flex-1 p-4 sm:p-6 lg:p-8 max-w-5xl">
          <div className="text-[10px] uppercase tracking-widest text-orange-600 font-bold">Espace dépanneur</div>
          <h1 className="font-display font-extrabold text-3xl text-slate-900 tracking-tight mb-4">Conformité & paiements</h1>

          {/* Bandeau d'éligibilité */}
          {elig && (
            <div className={`rounded-xl border p-4 mb-6 flex items-start gap-3 ${
              elig.eligible ? "bg-emerald-50 border-emerald-200" : "bg-red-50 border-red-200"
            }`} data-testid="eligibility-banner">
              {elig.eligible
                ? <ShieldCheck className="w-6 h-6 text-emerald-600 flex-shrink-0" />
                : <ShieldAlert className="w-6 h-6 text-red-600 flex-shrink-0" />}
              <div className="text-sm">
                <div className="font-display font-bold text-slate-900">
                  {elig.eligible ? "Vous êtes éligible aux missions" : "Inéligible aux nouvelles missions"}
                </div>
                <div className="text-slate-600 mt-0.5">
                  {elig.eligible
                    ? "Tous vos documents obligatoires sont vérifiés et à jour."
                    : "Un document obligatoire est manquant, en attente, refusé ou expiré. Complétez votre dossier ci-dessous pour enchérir à nouveau."}
                </div>
                {elig.expiring_soon?.length > 0 && (
                  <div className="text-amber-700 mt-1 text-xs">
                    À renouveler prochainement : {elig.expiring_soon.map(e => `${data.labels[e.doc_type]} (${e.expires_at})`).join(", ")}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Compte de paiement Stripe Connect */}
          <div className="bg-white border border-slate-200 rounded-xl p-5 mb-6">
            <div className="flex items-center justify-between gap-4 flex-wrap">
              <div className="flex items-start gap-3">
                <CreditCard className="w-6 h-6 text-orange-600 flex-shrink-0 mt-0.5" />
                <div>
                  <div className="font-display font-bold text-slate-900">Compte de reversement (Stripe Connect)</div>
                  <div className="text-sm text-slate-600 mt-0.5">
                    {!connect?.configured
                      ? "Stripe n'est pas configuré sur cet environnement — les reversements réels sont désactivés."
                      : connect?.payouts_enabled
                        ? "Compte actif : vos reversements arrivent sous 2 à 3 jours ouvrés après chaque livraison validée."
                        : connect?.account_id
                          ? "Onboarding à terminer pour recevoir vos reversements."
                          : "Créez votre compte de reversement pour être payé automatiquement (KYC sécurisé par Stripe)."}
                  </div>
                </div>
              </div>
              {connect?.configured && (
                <Button onClick={onboard} disabled={connectLoading} className="bg-orange-600 hover:bg-orange-700">
                  {connectLoading && <Loader2 className="w-4 h-4 animate-spin mr-1" />}
                  {connect?.payouts_enabled ? "Gérer mon compte" : connect?.account_id ? "Terminer l'onboarding" : "Configurer mes paiements"}
                </Button>
              )}
            </div>
          </div>

          {/* Documents réglementaires */}
          <div className="mb-2 flex items-baseline justify-between">
            <h2 className="font-display font-bold text-xl text-slate-900">Documents réglementaires</h2>
            <span className="text-xs text-slate-500"><span className="text-orange-600">*</span> obligatoire pour enchérir</span>
          </div>
          <p className="text-xs text-slate-500 mb-4">
            Chaque document est scellé numériquement (empreinte SHA-256 horodatée) puis vérifié par nos équipes.
            Un document expiré vous rend inéligible aux nouvelles missions jusqu'à renouvellement.
          </p>
          <div className="grid md:grid-cols-2 gap-3">
            {(data?.doc_types || []).map(t => (
              <DocRow
                key={t}
                docType={t}
                label={data.labels[t]}
                latest={latestByType[t]}
                required={data.required.includes(t)}
                onUploaded={load}
              />
            ))}
          </div>
        </main>
      </div>
    </div>
  );
}
